import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Form, Switch, Tooltip, message } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as EditIcon } from '../../../../../assets/svg/pen-to-square-solid.svg';
import {
  BREAKPOINTS,
  DATE_FORMATS,
  LIMIT,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  SORT,
} from '../../../../../common/constants';
import { formatDate, hasPermission } from '../../../../../common/utils';
import CommonTable from '../../../../../components/CommonTable';
import SearchComponent from '../../../../../components/SearchComponent';
import { UPDATE_PINCODE_DELIVERY_SCHEDULE } from '../../../../configs/modules/graphql/Mutations';
import { PINCODE_DELIVERY_SCHEDULES } from '../../../../configs/modules/graphql/Queries';
import PincodeDeliveryModal from './PincodeDeliveryModal';

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialSort = {
  field: 'pincode',
  order: SORT.ASC,
};

const DAY_NAMES = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const getDayName = (d) => DAY_NAMES[d] || '';

const getWeekName = (w) => {
  if (w === 1) return '1st';
  if (w === 2) return '2nd';
  if (w === 3) return '3rd';
  if (w === 4) return '4th';
  if (w === 5) return '5th';
  return `${w}th`;
};

const formatRecurrence = (recurrence) => {
  if (!recurrence) return '-';
  const { frequency, interval, anchorDate, daysOfWeek, monthlyMode, byMonthDays, byWeekday, customDates } = recurrence;

  if (frequency === 'DAILY') {
    return 'Daily';
  }

  if (frequency === 'WEEKLY') {
    const isAllDays = Array.isArray(daysOfWeek) && daysOfWeek.length === 7;
    const days = isAllDays ? 'All Days' : (daysOfWeek || []).map(getDayName).filter(Boolean).join(', ');
    const intervalStr = interval > 1 ? `Alternate (${interval} weeks)` : 'Weekly';
    return `${intervalStr} on ${days || '-'}${anchorDate ? ` starting ${anchorDate}` : ''}`;
  }

  if (frequency === 'MONTHLY') {
    const monthIntervalStr = interval > 1 ? `Monthly (every ${interval} months)` : 'Monthly';
    if (monthlyMode === 'BY_DATE') {
      const dates = (byMonthDays || []).map(getOrdinal).join(', ');
      return `${monthIntervalStr} on ${dates || '-'}${anchorDate ? ` starting ${anchorDate}` : ''}`;
    }
    if (monthlyMode === 'BY_WEEKDAY' && byWeekday) {
      let parsed = byWeekday;
      if (typeof byWeekday === 'string') {
        try {
          parsed = JSON.parse(byWeekday);
        } catch (e) {
          parsed = null;
        }
      }
      let listStr = '';
      if (Array.isArray(parsed) && parsed.length > 0) {
        listStr = parsed
          .map((item) => `${getWeekName(item?.week)} ${getDayName(item?.day)}`)
          .join(', ');
      } else if (parsed && typeof parsed === 'object') {
        const weekStr = getWeekName(parsed.week);
        const dayStr = getDayName(parsed.day);
        listStr = `${weekStr} ${dayStr}`;
      }
      return `${monthIntervalStr} on ${listStr || '-'}${anchorDate ? ` starting ${anchorDate}` : ''}`;
    }
    return `${monthIntervalStr}${anchorDate ? ` starting ${anchorDate}` : ''}`;
  }

  if (frequency === 'CUSTOM') {
    if (Array.isArray(customDates) && customDates.length > 0) {
      return `Custom dates: ${customDates.join(', ')}`;
    }
    return 'Custom dates';
  }

  return frequency || '-';
};


const PincodeDeliveryTable = ({ libraryId }) => {
  const {
    state: { permissions },
  } = useContext(AppContext);

  const [form] = Form.useForm();
  const [itemsList, setItemsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortedInfo, setSortedInfo] = useState({
    field: 'pincode',
    columnKey: 'pincode',
    order: 'ascend',
  });
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [isLoading, setIsLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedPincodesMap, setSelectedPincodesMap] = useState({});

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys, selectedRows) => {
      setSelectedRowKeys(selectedKeys);
      setSelectedPincodesMap((prevMap) => {
        const newMap = { ...prevMap };
        selectedRows.forEach((row) => {
          if (row?.pincode?.pincode) {
            newMap[row.id] = row.pincode;
          }
        });
        Object.keys(newMap).forEach((key) => {
          if (!selectedKeys.includes(key)) {
            delete newMap[key];
          }
        });
        return newMap;
      });
    },
    preserveSelectedRowKeys: true,
  };

  const [fetchPincodeDeliverySchedules] = useLazyQuery(
    PINCODE_DELIVERY_SCHEDULES,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const rows = data?.pincodeDeliverySchedules?.data;
        setItemsList(Array.isArray(rows) ? rows : []);
        const total = data?.pincodeDeliverySchedules?.count ?? 0;
        setPaginationProp((prev) => ({
          ...prev,
          total,
          current: prev?.current,
        }));
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
        setItemsList([]);
      },
    },
  );

  const buildSort = (sorter) => {
    const field = sorter?.field || sorter?.columnKey;
    if (sorter && field && sorter.order) {
      return {
        field,
        order: sorter.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
      };
    }
    return initialSort;
  };

  function fetchConfigs({
    skip = (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
    limit = paginationProp?.pageSize || LIMIT,
    search = searchTerm,
    sorter = sortedInfo,
  } = {}) {
    setIsLoading(true);
    const trimSearch = search?.trim();

    fetchPincodeDeliverySchedules({
      variables: {
        filter: {
          skip,
          limit,
          search: trimSearch || null,
          libraryId: libraryId || null,
        },
        sort: buildSort(sorter),
      },
    });
  }

  const [updateSchedule] = useMutation(UPDATE_PINCODE_DELIVERY_SCHEDULE, {
    onCompleted: (res) => {
      message.success(res?.updatePincodeDeliverySchedule?.message || 'Schedule updated successfully');
      fetchConfigs();
    },
    onError: (err) => {
      message.error(err?.message || 'Failed to update schedule');
    },
  });

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.MASTER_LIST_LIBRARY_INVENTORY_DETAILS,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  useEffect(() => {
    setSelectedRowKeys([]);
    setSelectedPincodesMap({});
    setPaginationProp((prev) => ({ ...prev, current: 1 }));
    fetchConfigs({
      skip: 0,
      limit: paginationProp?.pageSize || LIMIT,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const handleTableChange = (pagination, _tableFilter, sorter, extra) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);

    setPaginationProp((prev) => ({
      ...prev,
      ...pagination,
      current,
      pageSize: pagination?.pageSize || prev?.pageSize,
    }));

    let newSorter = sortedInfo;

    if (extra?.action === 'sort') {
      const clickedColumnKey = sorter?.columnKey || sortedInfo.columnKey || 'pincode';
      const clickedField = sorter?.field || sorter?.columnKey || sortedInfo.field || 'pincode';

      newSorter = {
        field: clickedField,
        columnKey: clickedColumnKey,
        order: 'ascend',
      };

      const isSameColumn = sorter?.columnKey ? (sorter.columnKey === sortedInfo.columnKey) : true;
      if (isSameColumn) {
        newSorter.order = sortedInfo.order === 'ascend' ? 'descend' : 'ascend';
      } else {
        newSorter.order = clickedColumnKey === 'createdAt' ? 'descend' : 'ascend';
      }

      setSortedInfo(newSorter);
    }

    fetchConfigs({
      skip,
      limit: pagination?.pageSize || LIMIT,
      sorter: newSorter,
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setSearchTerm(trimValue);
    setPaginationProp((prev) => ({ ...prev, current: 1, skip: 0 }));
    fetchConfigs({ skip: 0, search: trimValue });
  };

  const handleToggleActive = async (checked, record) => {
    if (checked && !record?.config?.recurrence) {
      message.error('Please configure schedule recurrence before activating');
      return;
    }
    try {
      await updateSchedule({
        variables: {
          where: { ids: [record.id] },
          data: { isActive: checked },
        },
      });
    } catch (e) {
      // handled in onError
    }
  };



  const columns = useMemo(
    () => [
      {
        title: 'PINCODE',
        dataIndex: 'pincode',
        key: 'pincode',
        ellipsis: true,
        width: 180,
        align: 'left',
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        sortOrder:
          sortedInfo?.columnKey === 'pincode' ? sortedInfo?.order : null,
        render: (_, record) => {
          const p = record?.pincode;
          if (!p?.pincode) return '-';
          return p.areaName ? `${p.pincode} (${p.areaName})` : p.pincode;
        },
      },
      {
        title: 'LIBRARY',
        dataIndex: 'libraryId',
        key: 'libraryId',
        ellipsis: true,
        width: 220,
        align: 'left',
        render: (_, record) =>
          record?.pincode?.library?.name || 'All Libraries',
      },
      {
        title: 'SCHEDULE RECURRENCE',
        key: 'recurrence',
        width: 320,
        align: 'left',
        render: (_, record) => formatRecurrence(record?.config?.recurrence),
      },
      {
        title: 'ACTIVE',
        dataIndex: 'isActive',
        key: 'isActive',
        width: 100,
        align: 'center',
        render: (isActive, record) => {
          const hasRecurrence = Boolean(record?.config?.recurrence);
          const switchComponent = (
            <Switch
              checked={isActive}
              disabled={!hasUpdatePermission || (!hasRecurrence && !isActive)}
              onChange={(checked) => handleToggleActive(checked, record)}
            />
          );

          if (!hasRecurrence && !isActive) {
            return (
              <Tooltip
                title="Configure recurrence schedule first"
                placement="top"
              >
                <span>{switchComponent}</span>
              </Tooltip>
            );
          }

          return switchComponent;
        },
      },
      {
        title: 'CREATED AT',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 160,
        align: 'left',
        ellipsis: true,
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        sortOrder: sortedInfo?.columnKey === 'createdAt' ? sortedInfo?.order : null,
        render: (_, record) =>
          formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      },
      {
        title: 'ACTIONS',
        dataIndex: 'actions',
        key: 'actions',
        width: 120,
        align: 'left',
        // eslint-disable-next-line no-undef
        fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
        render: (_, record) => (
          <div className="action-button">
            {hasUpdatePermission && (
              <Tooltip title="Edit" placement="top" zIndex={4}>
                <EditIcon
                  className="mr-16 pointer svg-icon"
                  onClick={() => {
                    setCurrentItem(record);
                    setIsModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [hasUpdatePermission, sortedInfo],
  );

  const locale = {
    emptyText: isLoading ? <span /> : '',
    triggerDesc: 'Click to sort descending',
    triggerAsc: 'Click to sort ascending',
  };

  return (
    <>
      <PincodeDeliveryModal
        form={form}
        item={currentItem}
        selectedIds={selectedRowKeys}
        selectedPincodes={Object.values(selectedPincodesMap)}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        onClose={() => setCurrentItem(null)}
        onFinish={() => {
          setIsModalOpen(false);
          setCurrentItem(null);
          setSelectedRowKeys([]);
          setSelectedPincodesMap({});
          fetchConfigs();
        }}
      />

      <div className="d-flex justify-end align-center mb-16 flex-wrap">
        <Button
          type="primary"
          className="common-button mr-16"
          disabled={!hasUpdatePermission || selectedRowKeys.length <= 1}
          onClick={() => {
            setCurrentItem(null);
            setIsModalOpen(true);
          }}
        >
          Bulk Edit
          {selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
        </Button>
        <SearchComponent
          className="search-box"
          query={searchTerm}
          setQuery={setSearchTerm}
          getData={handleSearch}
          name="Pincode"
        />
      </div>

      <CommonTable
        locale={locale}
        columns={columns}
        data={itemsList || []}
        loadingData={isLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
        scroll={{ x: 'max-content' }}
        rowSelection={hasUpdatePermission ? rowSelection : undefined}
      />
    </>
  );
};

export default PincodeDeliveryTable;
